/**
 * Business Registration Page - Green & White Theme
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import api from '../lib/api';
import { Link2, ArrowLeft, Store, Loader2 } from 'lucide-react';

const BusinessRegister = () => {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState('');
  const [trn, setTrn] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [parish, setParish] = useState('');
  const [isPudo, setIsPudo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await api.post('/business/register', {
        business_name: businessName,
        trn: trn || null,
        is_pudo: isPudo,
        addresses: [{
          street,
          city,
          parish
        }]
      });
      toast.success('Business registered successfully!');
      navigate('/business');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-emerald-100 px-6 py-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-emerald-600 hover:bg-emerald-50 p-2">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Link2 className="h-6 w-6 text-emerald-600" />
          <span className="font-bold text-lg text-gray-800">Register Business</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-md mx-auto">
          {/* Icon */}
          <div className="text-center mb-8">
            <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Store className="h-10 w-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">Register Your Business</h1>
            <p className="text-gray-600 mt-2">Join 's merchant network</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-gray-700">Business Name</Label>
              <Input
                data-testid="business-name-input"
                type="text"
                placeholder="Your Business Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="bg-white border-gray-200 h-12 rounded-xl"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">TRN (Tax Registration Number)</Label>
              <Input
                data-testid="trn-input"
                type="text"
                placeholder="123-456-789"
                value={trn}
                onChange={(e) => setTrn(e.target.value)}
                className="bg-white border-gray-200 h-12 rounded-xl"
              />
              <p className="text-xs text-gray-500">Optional but required for approved status</p>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <Label className="text-gray-700 mb-3 block">Business Address</Label>
              <div className="space-y-3">
                <Input
                  data-testid="street-input"
                  type="text"
                  placeholder="Street Address"
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  className="bg-white border-gray-200 h-12 rounded-xl"
                  required
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    data-testid="city-input"
                    type="text"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="bg-white border-gray-200 h-12 rounded-xl"
                    required
                  />
                  <Input
                    data-testid="parish-input"
                    type="text"
                    placeholder="Parish"
                    value={parish}
                    onChange={(e) => setParish(e.target.value)}
                    className="bg-white border-gray-200 h-12 rounded-xl"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <Checkbox
                id="pudo"
                data-testid="pudo-checkbox"
                checked={isPudo}
                onCheckedChange={setIsPudo}
                className="border-emerald-500 data-[state=checked]:bg-emerald-500"
              />
              <div className="flex-1">
                <Label htmlFor="pudo" className="text-gray-800 font-medium cursor-pointer">
                  Enable as PUDO Point
                </Label>
                <p className="text-xs text-gray-500">Accept package pickups and drop-offs</p>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="register-business-btn"
              disabled={isLoading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl h-12 font-medium mt-4"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registering...
                </>
              ) : (
                'Register Business'
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default BusinessRegister;
